import { FC, useMemo, useCallback } from 'react'
import * as styles from 'components/features/pfm/CategoryBreakdown/CategoryBreakdown.styl'
import { MoneyflowBreakdownItem } from 'interfaces/moneyflow'
import { BreakdownChart } from 'components/features/pfm/CategoryBreakdown/BreakdownChart'
import SvgIcon from 'components/atoms/SvgIcon'
import { Category } from 'interfaces/meta'
import { getTrackerCategoryName } from 'helpers/categoriesHelpers'
import { formatUSCurrency } from 'helpers/formatHelpers'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import Button from 'components/features/pfm/Button/Button'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { setFilterCategoryActionCreator } from 'actions/globalActions'

type CategoryBreakdownProps = {
  member: MoneyflowBreakdownItem
  peers: MoneyflowBreakdownItem
  other: MoneyflowBreakdownItem
  maxTotal: number
  category: Category
  withComparison: boolean
}

export const CategoryBreakdown: FC<CategoryBreakdownProps> = (props) => {
  const {
    member,
    peers,
    other,
    maxTotal,
    category,
    withComparison,
  } = props

  const amount = useMemo(() => ({
    memberValue: member.amount,
    peerValue: peers.amount,
    otherValue: other.amount,
  }), [member, peers, other])

  const projected = useMemo(() => {
    if (!member.projected || !peers.projected || !other.projected) {
      return
    }

    return {
      memberValue: member.projected,
      peerValue: peers.projected,
      otherValue: other.projected,
    }
  }, [member, peers, other])

  const iconChild = useMemo(() => {
    const iconSource = require(`icons/${ category.id }.svg`).default
    return (
      <SvgIcon src={ iconSource } />
    )
  }, [category])

  const categoryName = useMemo(() => getTrackerCategoryName(category, 'spending'), [category])

  const difference = useMemo(() => {
    if (!projected) {
      return 0
    }

    return projected.memberValue - projected.peerValue
  }, [projected])

  const className = useMemo(() => {
    return cn(styles.categoryBreakdown, {
      [styles.great]: difference >= 0,
      [styles.less]: difference < 0,
      [styles.hideComparison]: !withComparison,
    })
  }, [difference, withComparison])

  const dispatch = useStoreDispatch()

  const handleClick = useCallback(() => {
    dispatch(setFilterCategoryActionCreator('spending', category.id))
  }, [category.id, dispatch])

  return (
    <Link to='/spending' title={ `Learn more about your ${ category.value } spending` } onClick={ handleClick }>
      <div className={ className }>
        <div className={ cn(styles.row, styles.header) }>
          <div className={ styles.side }>
            <div className={ styles.icon }>
              { iconChild }
            </div>
          </div>
          <div className={ styles.middle }>
            { categoryName }
          </div>
          <div className={ styles.side }>
            <Button
              style='textOnly'
              size='small'
              fullHeight
            >
              TREND
            </Button>
          </div>
        </div>

        <div className={ cn(styles.row, styles.difference) }>
          <div className={ styles.side } />
          <div className={ styles.middle }>
            { difference > 0 && (
              <>
                <span className={ styles.value }>{ formatUSCurrency(difference) }</span> more than your peers
              </>
            )}
            { difference < 0 && (
              <>
                <span className={ styles.value }>{ formatUSCurrency(Math.abs(difference)) }</span> less than your peers
              </>
            )}
          </div>
        </div>
        <div className={ cn(styles.row, styles.content) }>
          <div className={ styles.side }>
            { formatUSCurrency(amount.memberValue) }
            <div className={ styles.note }>Actual</div>
          </div>
          <div className={ styles.middle }>
            <BreakdownChart
              amount={ amount }
              projected={ projected }
              maxTotal={ maxTotal }
              withComparison={ withComparison }
            />
          </div>
          <div className={ styles.side }>
            { projected && (
              <>
                { formatUSCurrency(projected.memberValue) }
                <div className={ styles.note }>Projected</div>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}
